import type { SubscriptionSourceMetadata } from '@parcel2go/npm-p2g-app';

export type SubscriptionTier = 'plus' | 'pro';
export type SubscriptionInterval = 'month' | 'year';

/**
 * Paid tiers, cheapest first. The order is product vocabulary rather than catalogue data - Stripe
 * has no concept of one product ranking above another - so it lives beside the tier type and is the
 * one place that knows which way is up.
 */
export const TIER_ORDER: readonly SubscriptionTier[] = ['plus', 'pro'];

/**
 * The tier's display name - "Plus"/"Pro". These are Stripe product names, deliberately
 * untranslated (the same convention ui-checkout uses for its own tier badges), just capitalised.
 *
 * Here rather than beside one call site because the plan picker and onboarding's own heading must
 * spell the tier the same way - a picker offering "Pro" above a heading saying "pro" is two names
 * for one product.
 */
export function tierLabel(tier: SubscriptionTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** Whether a tier is the highest we sell, i.e. there is nothing to upgrade to from here. */
export function isTopTier(tier: SubscriptionTier): boolean {
  return TIER_ORDER[TIER_ORDER.length - 1] === tier;
}

/**
 * Which single plan a surface is aimed at - either a tier/interval pair or a Stripe Price lookup
 * key naming it directly. The two spellings are ui-checkout's own (`tier=&interval=` or `plan=`);
 * whichever is easier to construct is accepted.
 *
 * Deliberately ONE type rather than a near-identical copy per consumer. The pending-plan hook and
 * the checkout frame both take this exact shape, and two structurally identical declarations would
 * drift silently - TypeScript cannot tell them apart, so adding a field to one keeps compiling
 * against the other.
 */
export type SubscriptionPlanTarget =
  | { tier: SubscriptionTier; interval: SubscriptionInterval; plan?: never }
  | { plan: string; tier?: never; interval?: never };

export interface PlanCard {
  /** Stripe's own id for this price - the handle a held subscription can be compared against. */
  priceId: string;
  tier: SubscriptionTier;
  interval: SubscriptionInterval;
  lookupKey: string | null;
  /**
   * Minor units (pence). For an ANNUAL plan this is the full year's amount, never a per-month
   * figure. Format it through `formatSubscriptionPrice` (pricing.ts) rather than dividing here.
   * Non-null by construction: `usePlanCards` drops a plan with no amount.
   */
  priceMinorUnits: number;
  currency: string;
}

/** Raw wire shape of one entry in `GET {PAYMENT_API}/pp/stripe/subscription-plans` - one per Stripe
 * price, not per tier (a tier has both a monthly and an annual entry). */
export interface RawSubscriptionPlan {
  priceId: string;
  lookupKey: string | null;
  productName: string | null;
  unitAmount: number | null;
  currency: string;
  interval: string | null;
}

export interface SubscriptionPlansResult {
  plans: RawSubscriptionPlan[];
  trialDays: number;
  publishableKey: string;
}

/**
 * The package's subscription billing detail plus the tax fields the Payments API now sends on it.
 * Stand-in until `SubscriptionSourceMetadata` in `@parcel2go/npm-p2g-app` declares them - delete
 * this and use the package type once it does.
 *
 * Both are optional as well as nullable. `null` means the subscription is sold untaxed, but a
 * subscription whose billing detail was stored before the API carried tax has neither property
 * until it is next synced, so absent is not the same as untaxed.
 */
export type SubscriptionWithTax = SubscriptionSourceMetadata & {
  /** The rate the subscription is taxed at, as a percentage - `20`, not `0.2`. */
  taxPercentage?: number | null;
  /**
   * What the card is charged per interval, in minor units. `unitAmount` is the price before tax;
   * where this is null or absent, `unitAmount` is the only figure there is.
   */
  unitAmountIncludingTax?: number | null;
};
